import {
  Bank,
  type BankAccountListItem,
  BankAccountStatus,
  BankEnvironment,
} from "Infrastructure/Api/Api";
import { composeIBAN, extractIBAN } from "ibantools";

// Groups an IBAN into space-separated blocks of four for display (e.g. "CZ65 0800 0000 1920 0014 5399")
export const formatIBAN = (IBAN: string): string =>
  IBAN.match(/.{1,4}/g)?.join(" ") ?? IBAN;

// CZ and SK have the same format of IBAN (XXkk bbbb pppp ppcc cccc cccc)
export const getLocalBankAccountNumberFromIBAN = (IBAN: string): string => {
  const { valid, iban, countryCode } = extractIBAN(IBAN);

  if (!valid || (countryCode !== "CZ" && countryCode !== "SK")) {
    return IBAN;
  }

  const bank = iban.slice(4, 8);

  const prefix = iban.slice(8, 14);
  const account = iban.slice(14, 24);

  const hasPrefix = !/^0+$/.test(prefix);
  if (hasPrefix) {
    return `${prefix}-${account}/${bank}`;
  }
  return `${account}/${bank}`;
};

export const getIBANFromLocalBankAccountNumber = (
  accountNumber: string,
  countryCode: "CZ" | "SK",
): string => {
  const [accPart, bank] = accountNumber.split("/");

  if (!accPart || !bank || bank.length !== 4) {
    return "";
  }

  let prefix = "000000";
  let account = accPart;

  // If account number contains prefix (e.g. "19-2000145399")
  if (accPart.includes("-")) {
    const [rawPrefix, rawAccount] = accPart.split("-");
    prefix = rawPrefix.padStart(6, "0");
    account = rawAccount;
  }

  // Validate lengths
  if (account.length > 10 || prefix.length > 6) {
    return "";
  }

  // Ensure account number is padded to 10 digits
  const paddedAccount = account.padStart(10, "0");
  // Construct BBAN: bank(4) + prefix(6) + account(10)
  const bban = `${bank}${prefix}${paddedAccount}`;
  return composeIBAN({ countryCode, bban }) || "";
};

const ALLOWED_ACCOUNT_STATUSES = [
  BankAccountStatus.Active,
  BankAccountStatus.Restricted,
  BankAccountStatus.PreApproved,
];

export const hasPreApprovedAccount = (
  accounts: ReadonlyArray<BankAccountListItem>,
): boolean =>
  accounts.some(({ status }) => status === BankAccountStatus.PreApproved);

/**
 * Picks the account a money flow should default to: the primary of the given currency,
 * else the best-ranked usable account of that currency. Primary is per currency, so the
 * caller must say which rail it is about (transaction currency, market currency, ...).
 * An unknown currency returns undefined — CTAs disable rather than guess a rail.
 */
export const pickMainBankAccount = (
  accounts: Array<BankAccountListItem>,
  currency: string | undefined,
): BankAccountListItem | undefined => {
  if (!currency) {
    return undefined;
  }

  const currencyAccounts = accounts.filter(
    (account) => account.currency === currency,
  );

  return (
    currencyAccounts.find(({ isPrimary }) => isPrimary) ||
    currencyAccounts
      .filter(({ status }) => ALLOWED_ACCOUNT_STATUSES.includes(status))
      .toSorted((a, b) => b.rank - a.rank)?.[0]
  );
};

/**
 * Partitions accounts into per-currency groups for the account list — the user's
 * market currency first, other currencies alphabetically. The incoming (rank) order
 * is preserved within each group.
 */
export const groupAccountsByCurrency = (
  accounts: Array<BankAccountListItem>,
  marketCurrency: string | undefined,
): Array<{ currency: string; accounts: Array<BankAccountListItem> }> => {
  const groups = accounts.reduce<Map<string, Array<BankAccountListItem>>>(
    (map, account) => {
      const group = map.get(account.currency);
      if (group) {
        group.push(account);
      } else {
        map.set(account.currency, [account]);
      }
      return map;
    },
    new Map(),
  );

  return [...groups.entries()]
    .map(([currency, accounts]) => ({ currency, accounts }))
    .toSorted((a, b) => {
      if (a.currency === marketCurrency) {
        return -1;
      }
      if (b.currency === marketCurrency) {
        return 1;
      }
      return a.currency.localeCompare(b.currency);
    });
};

export const getBankCodeFromIBAN = (iban: string): string => {
  const localAccountNumber = getLocalBankAccountNumberFromIBAN(iban);
  const [_, bank] = localAccountNumber.split("/");
  return bank;
};

export const BANK_CODE_TO_BANK: Record<string, Bank> = {
  "0300": Bank.CSOB,
  "0800": Bank.CSAS,
  "0100": Bank.KB,
  "3030": Bank.AirBank,
  "2700": Bank.UCB,
  "0600": Bank.MONETA,
  "2010": Bank.FIO,
  "6210": Bank.MBank,
  "5500": Bank.RB,
  "2060": Bank.Citfin,
  "2070": Bank.TRINITY,
  "2200": Bank.PenezniDum,
  "2220": Bank.Artesa,
  "2250": Bank.CREDITAS,
  "2260": Bank.NEY,
  "4000": Bank.MaxBanka,
  "4300": Bank.NRB,
  "5800": Bank.JandT,
  "6000": Bank.PPF,
  "8040": Bank.Oberbank,
  "6363": Bank.PartnersBank,
  "0710": Bank.CNB,
  REVO: Bank.Revolut,
} as const;

export const BANK_TO_ENVIRONMENT: Record<Bank, BankEnvironment> = {
  [Bank.AirBank]: BankEnvironment.AirBank_PROD,
  [Bank.CSOB]: BankEnvironment.CSOB_PROD,
  [Bank.CSAS]: BankEnvironment.CSAS_PROD,
  [Bank.KB]: BankEnvironment.KB_PROD,
  [Bank.MBank]: BankEnvironment.MBANK_PROD,
  [Bank.MaxBanka]: BankEnvironment.MaxBanka_PROD,
  [Bank.MONETA]: BankEnvironment.MONETA_PROD,
  [Bank.Oberbank]: BankEnvironment.Oberbank_PROD,
  [Bank.PartnersBank]: BankEnvironment.PartnersBank_PROD,
  [Bank.PPF]: BankEnvironment.PPF_PROD,
  [Bank.RB]: BankEnvironment.RB_PROD,
  [Bank.TRINITY]: BankEnvironment.TRINITY_PROD,
  [Bank.PenezniDum]: BankEnvironment.PenezniDum_PROD,
  [Bank.Artesa]: BankEnvironment.Artesa_PROD,
  [Bank.CREDITAS]: BankEnvironment.CREDITAS_PROD,
  [Bank.NEY]: BankEnvironment.NEY_PROD,
  [Bank.NRB]: BankEnvironment.NRB_PROD,
  [Bank.JandT]: BankEnvironment.JandT_PROD,
  [Bank.CNB]: BankEnvironment.CNB_PROD,
  [Bank.FIO]: BankEnvironment.FIO_PROD,
  [Bank.UCB]: BankEnvironment.UCB_PROD,
  [Bank.Revolut]: BankEnvironment.Revolut_PROD,
  [Bank.Citfin]: BankEnvironment.Citfin_PROD,
  [Bank.CZKT]: BankEnvironment.CZKT_PROD,
} as const;
